import { ObservableProp, useClassStore } from '@Utils/useClassStore';
import { inject, injectable } from 'inversify';
import { action } from 'mobx';

import container from '~ioc/ioc';
import AuthStore from '~stores/authentication';
import toastStore from '~stores/toast';

@injectable()
class PasswordChangeModalPresenter {
  @inject(AuthStore) private _authStore!: AuthStore;

  visible = new ObservableProp(false);
  email = new ObservableProp('');
  password = new ObservableProp('');
  passwordConfirm = new ObservableProp('');
  token = new ObservableProp('');

  show = (token: string) => {
    this.visible.set(true);
    this.token.set(token);
  };

  hide = () => {
    this.visible.set(false);
    this.password.set('');
    this.passwordConfirm.set('');
  };

  @action updateUserPassword = async () => {
    const pswd = this.password.value?.trim();
    const pswdConfirm = this.passwordConfirm.value?.trim();

    if (!this.token.value) {
      toastStore.addMessage({
        message: 'Invalid password reset link',
        type: 'error',
      });
      // If the link is invalid hide the modal
      this.hide();
      return;
    }

    if (!pswd && !pswdConfirm) {
      toastStore.addMessage({
        message: 'Please provide all the fields',
        type: 'error',
      });
      return;
    }

    if (pswd !== pswdConfirm) {
      toastStore.addMessage({
        message: 'Passwords do not match.',
        type: 'error',
      });
      return;
    }

    const response = await this.sendPasswordResetRequest({
      password: pswd,
      confirmPassword: pswdConfirm,
      token: this.token.value,
    });

    if (!response.success) {
      toastStore.addMessage({
        message: response.errorMessage,
        type: 'error',
      });
      return;
    }

    toastStore.addMessage({
      message: 'Password Successfully Updated',
    });
    this.hide();
  };

  private sendPasswordResetRequest = async (params: {
    password: string;
    confirmPassword: string;
    token: string;
  }): Promise<{ success: boolean; errorMessage?: string }> => {
    try {
      await this._authStore.acceptPasswordReset(params);
      return { success: true };
    } catch (error) {
      return { success: false, errorMessage: (error as Error).message };
    }
  };
}

export const usePasswordChangeModalPresenter = () =>
  useClassStore<PasswordChangeModalPresenter>(
    container.get(PasswordChangeModalPresenter)
  );
